/**
 * Dialogs under the iOS on-screen keyboard.
 *
 * iOS Safari shrinks `window.visualViewport` when the keyboard opens but NOT the layout viewport, so `100vh` / `100dvh`
 * do not change and a dialog sized to them has its lower half under the keyboard with nothing left to scroll. Every modal
 * in the app (`[aria-modal="true"]`) is sized from two custom properties this module keeps on `:root`:
 *
 *   --vv-height      the visible height, px   (CSS falls back to 100dvh when unset: no visualViewport, SSR, old browsers)
 *   --vv-offset-top  how far iOS has scrolled the visual viewport inside the layout viewport, px
 *
 * `src/styles/dialog-viewport.css` consumes them. This module also scrolls a focused field into the visible band once
 * the keyboard has finished animating. Installed once from main.tsx; one mechanism for every dialog, so a new dialog needs
 * nothing as long as it is `aria-modal`.
 */

const TEXT_ENTRY = "input:not([type=checkbox]):not([type=radio]):not([type=button]):not([type=submit]):not([type=hidden]), select, textarea";
/** How long the keyboard animation is given before the focused field is scrolled into view. */
const KEYBOARD_SETTLE_MS = 300;

function insideModal(el: Element | null): el is HTMLElement {
  return el instanceof HTMLElement && el.matches(TEXT_ENTRY) && el.closest('[aria-modal="true"]') !== null;
}

/** Writes the two custom properties; skips the write when nothing changed. */
export function syncViewportVars(root: HTMLElement, vv: Pick<VisualViewport, "height" | "offsetTop">): void {
  const height = `${Math.round(vv.height)}px`;
  const top = `${Math.max(0, Math.round(vv.offsetTop))}px`;
  if (root.style.getPropertyValue("--vv-height") !== height) root.style.setProperty("--vv-height", height);
  if (root.style.getPropertyValue("--vv-offset-top") !== top) root.style.setProperty("--vv-offset-top", top);
}

/** Starts the listeners and returns the function that removes them. A no-op where `visualViewport` is missing. */
export function installDialogViewport(): () => void {
  const vv = window.visualViewport;
  if (!vv) return () => undefined;
  const root = document.documentElement;
  let frame = 0;

  // A scroll inside a modal while a reveal is pending means the person (or the page) already positioned the dialog; the
  // reveal must not drag it back to the focused field. Without this, a late reveal undid a scroll-to-the-Save-button.
  let scrolledSinceScheduled = false;
  const onScroll = (e: Event): void => {
    if (frame && e.target instanceof Element && e.target.closest('[aria-modal="true"]')) scrolledSinceScheduled = true;
  };

  const cancelPending = (): void => {
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
  };

  const reveal = (el: HTMLElement): void => {
    if (el.isConnected && document.activeElement === el) el.scrollIntoView({ block: "center", inline: "nearest" });
  };

  /** A requestAnimationFrame loop (no timers): waits out the keyboard animation, then scrolls the field into view. */
  const revealAfterKeyboard = (el: HTMLElement): void => {
    cancelPending();
    scrolledSinceScheduled = false;
    const started = performance.now();
    const tick = (now: number): void => {
      frame = 0;
      if (!el.isConnected || document.activeElement !== el) return;
      if (now - started < KEYBOARD_SETTLE_MS) {
        frame = requestAnimationFrame(tick);
        return;
      }
      if (!scrolledSinceScheduled) reveal(el);
    };
    frame = requestAnimationFrame(tick);
  };

  // Only a resize (the keyboard opening or closing) re-reveals; the visual viewport also fires scroll while the person
  // pans it, and snapping them back to the focused field on every pan fights their finger.
  const onViewportScroll = (): void => syncViewportVars(root, vv);
  const onViewportChange = (): void => {
    syncViewportVars(root, vv);
    // The keyboard opening resizes the visual viewport AFTER focus lands; keep the focused field in the visible band.
    const active = document.activeElement;
    if (insideModal(active)) revealAfterKeyboard(active);
  };
  const onFocusIn = (e: FocusEvent): void => {
    if (insideModal(e.target as Element | null)) revealAfterKeyboard(e.target as HTMLElement);
  };

  syncViewportVars(root, vv);
  vv.addEventListener("resize", onViewportChange);
  vv.addEventListener("scroll", onViewportScroll);
  document.addEventListener("focusin", onFocusIn);
  document.addEventListener("scroll", onScroll, true);
  return () => {
    cancelPending();
    vv.removeEventListener("resize", onViewportChange);
    vv.removeEventListener("scroll", onViewportScroll);
    document.removeEventListener("focusin", onFocusIn);
    document.removeEventListener("scroll", onScroll, true);
    root.style.removeProperty("--vv-height");
    root.style.removeProperty("--vv-offset-top");
  };
}
